import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type ApiRequestOptions = {
    stream?: false;
};

type StreamApiRequestOptions = {
    stream: true;
};

type ApiRequest = {
    <T = unknown>(
        endpoint: string,
        method?: HttpMethod,
        body?: unknown,
        options?: ApiRequestOptions,
    ): Promise<T>;
    (
        endpoint: string,
        method: HttpMethod,
        body: unknown,
        options: StreamApiRequestOptions,
    ): Promise<ReadableStream<Uint8Array> | null>;
};

type AuthContextValue = {
    isLoggedIn: boolean;
    saveTokens: () => void;
    logout: () => void;
    apiRequest: ApiRequest;
};

type AuthProviderProps = {
    children: ReactNode;
};

type RefreshResponse = {
    success: boolean;
};

type HttpError = Error & {
    status: number;
};

const MAX_REQUEST_SIZE = 1024 * 1024 * 20;

const AuthContext = createContext<AuthContextValue | null>(null);

class RequestSizeError extends Error {
    readonly size: number;
    readonly maxSize: number;

    constructor(size: number, maxSize: number) {
        super('Request size exceeds the maximum limit.');
        this.name = 'RequestSizeError';
        this.size = size;
        this.maxSize = maxSize;
    }
}

function getInitialAuthenticationState(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }

    const hasAccessCookie = document.cookie.includes(
        'access_token_cookie=',
    );

    return (
        sessionStorage.getItem('is_authenticated') === 'true' ||
        hasAccessCookie
    );
}

function AuthProvider({ children }: AuthProviderProps) {
    const { i18n } = useTranslation();
    const [isAuthenticated, setIsAuthenticated] = useState(
        getInitialAuthenticationState,
    );

    const logout = useCallback(() => {
        const url =
            window.location.pathname +
            window.location.search +
            window.location.hash;

        fetch('/api/navigation/log', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
                url,
                event: 'LOGOUT',
            }),
            keepalive: true,
        }).catch(() => undefined);

        fetch('/api/auth/logout', {
            method: 'POST',
            credentials: 'include',
        })
            .catch(() => undefined)
            .finally(() => {
                sessionStorage.removeItem('is_authenticated');
                setIsAuthenticated(false);
            });
    }, []);

    const refreshAccessToken = useCallback(async (): Promise<void> => {
        const response = await fetch('/api/auth/refresh', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            logout();
            throw new Error('Unable to refresh the access token.');
        }

        const data = await response.json() as RefreshResponse;

        if (!data.success) {
            logout();
            throw new Error('Refresh token invalid.');
        }

        sessionStorage.setItem('is_authenticated', 'true');
        setIsAuthenticated(true);
    }, [logout]);

    const apiRequest = useCallback(
        async <T,>(
            endpoint: string,
            method: HttpMethod = 'GET',
            body: unknown = null,
            options: ApiRequestOptions | StreamApiRequestOptions = {},
        ): Promise<T | ReadableStream<Uint8Array> | null> => {
            async function doRequest(): Promise<Response> {
                const content = body === null
                    ? undefined
                    : JSON.stringify(body);

                if (
                    content !== undefined &&
                    content.length > MAX_REQUEST_SIZE
                ) {
                    throw new RequestSizeError(
                        content.length,
                        MAX_REQUEST_SIZE,
                    );
                }

                return fetch(endpoint, {
                    method,
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Language': i18n.language,
                    },
                    credentials: 'include',
                    body: content,
                });
            }

            let response = await doRequest();

            if (response.status === 401) {
                await refreshAccessToken();
                response = await doRequest();
            }

            if (!response.ok) {
                const error = new Error(
                    `HTTP error ${response.status}`,
                ) as HttpError;

                error.status = response.status;
                throw error;
            }

            if (options.stream) {
                return response.body;
            }

            return response.json() as Promise<T>;
        },
        [i18n.language, refreshAccessToken],
    ) as ApiRequest;

    const saveTokens = useCallback(() => {
        sessionStorage.setItem('is_authenticated', 'true');
        setIsAuthenticated(true);
    }, []);

    const value = useMemo<AuthContextValue>(() => ({
        isLoggedIn: isAuthenticated,
        saveTokens,
        logout,
        apiRequest,
    }), [isAuthenticated, saveTokens, logout, apiRequest]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

function useAuth(): AuthContextValue {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error('useAuth must be used inside an AuthProvider.');
    }

    return context;
}

export { AuthProvider, RequestSizeError, useAuth };
export type { ApiRequest, AuthContextValue, HttpMethod };
export default useAuth;
